<template>
  <div class="media-form informer">
    <HeadSection :title="isEditMode ? `Item #${informerId}` : 'Create new informer'" permissionOfSection="informers"
      :breadcrumbs="breadcrumbs" :uuid="informerUuid">
      <template #activeSwitch>
        <div class="ml-4">
          <a-switch v-model="form.isActive" />
        </div>
      </template>
      <template #actions>
        <a-col :span="14" class="text-right d-flex justify-content-end">
          <div v-if="isEditMode">
            <a-popconfirm title="Sure to delete?" placement="bottom" @confirm.stop="deleteInformer">
              <a-button :loading="loading" type="danger"> Delete </a-button>
            </a-popconfirm>
            <a-button :loading="loading" type="primary" class="ml-4" @click="save">
              Update
            </a-button>
          </div>
          <div v-else class="pt-4">
            <a-button :loading="loading" type="primary" class="ml-4" @click="save">
              Save
            </a-button>
          </div>
        </a-col>
      </template>
    </HeadSection>
    <MainContainer :loading="mainLoading || userLoading" permissionOfSection="informers">
      <a-form-model :rules="rules" :model="form" ref="form">
        <div class="media-form__main">
          <span class="font-weight-bold font-size-16 mb-1">General</span>
          <a-row>
            <a-col :span="6">
              <a-form-model-item prop="name" :colon="false">
                <template #label>Name <span class="text-gray-7" style="font-weight: 400;">(for internal
                    usage)</span></template>
                <a-input v-model="form.name" placeholder="Name" size="large" :disabled="loading" />
              </a-form-model-item>
            </a-col>
          </a-row>
          <a-row>
            <a-col :span="6">
              <a-form-model-item prop="extId" :colon="false">
                <template #label>External ID</template>
                <a-input v-model="form.extId" placeholder="External id" size="large" :disabled="loading" />
              </a-form-model-item>
            </a-col>
          </a-row>
          <a-row>
            <a-col :span="6">
              <a-form-model-item prop="contentType" label="Content type">
                <a-select v-model="form.contentType" size="large" :disabled="loading" allow-clear>
                  <template #placeholder><span style="color: #a1a1c2">Select content type</span></template>
                  <a-select-option v-for="contentType in contentTypeOptions" :key="contentType.value"
                    :value="contentType.value">
                    {{ contentType.label }}
                  </a-select-option>
                </a-select>
              </a-form-model-item>
            </a-col>
          </a-row>
          <ActivityPeriods
            ref="activity-periods"
            :periods="informerToEdit?.context.periods"
            :showIndex="false"
            :loading="loading"
            class="mb-2"
            buttonPos="bottom"
            showLabel
            useTimezone
          >
            <template #label>
              Show Period
            </template>
            <template #buttonText>
              + Add new period
            </template>
          </ActivityPeriods>
          <a-row>
            <a-col :span="6">
              <a-form-model-item prop="informerTag">
                <template #label>Tag</template>
                <a-select v-model="form.informerTag" size="large" :disabled="loading" allow-clear>
                  <a-select-option v-for="tag in tags" :key="tag.value" :value="tag.value">
                    <div class="informer-form__tag-select">
                      {{ tag.name }}
                    </div>
                  </a-select-option>
                </a-select>
              </a-form-model-item>
            </a-col>
          </a-row>
          <a-form-model-item label="Section" prop="sectionType" :colon="false" style="margin-bottom:  8px;">
            <a-radio-group name="radioGroup" v-model="form.sectionType" :default-value="form.sectionType"
              :disabled="loading || isSectionTypeDisabled" @change="handleSectionTypeChange">
              <a-radio value="viewscreens"> Viewscreens </a-radio>
              <a-radio value="screens"> Screens </a-radio>
            </a-radio-group>
          </a-form-model-item>
          <a-row v-if="form.sectionType === 'viewscreens'">
            <a-col :span="6">
              <a-form-model-item label="Viewscreens" prop="selectedViewscreens" :colon="false">
                <a-select v-model="form.selectedViewscreens" size="large" :disabled="loading" mode="multiple" showArrow
                  allow-clear @change="clearApps"
                  :filterOption="(input, option) => option.componentOptions.children[0].text.toLowerCase().indexOf(input.toLowerCase()) >= 0">
                  <template #placeholder><span style="color: #a1a1c2;">Select viewscreen</span></template>
                  <a-select-option v-for="viewscreen in viewscreens" :key="viewscreen.id">
                    {{ viewscreen.name }}
                  </a-select-option>
                </a-select>
              </a-form-model-item>
            </a-col>
          </a-row>
          <div v-else>
            <a-row class="mb-3">
              <a-col :span="6">
                <a-form-model-item label="Screens" prop="sections" :colon="false" class="mb-3">
                  <a-select v-model="form.sections" size="large" mode="multiple" showArrow :disabled="loading"
                    allow-clear>
                    <template #placeholder><span style="color: #a1a1c2;">Select screens</span></template>
                    <a-select-option v-for="section in sections" :key="section.value" :value="section.value">
                      {{ section.label }}
                    </a-select-option>
                  </a-select>
                </a-form-model-item>
              </a-col>
            </a-row>
            <a-row>
              <a-col :span="6">
               <a-form-model-item prop="segments" :colon="false">
                  <template #label>
                    <SegmentsLabel />
                  </template>
                  <div class="d-flex align-items-center w-full">
                    <SegmentsSelect v-model="form.segments" :segments="segments" :loading="loading"
                      @change="validateSegments" />
                  </div>
                </a-form-model-item>
              </a-col>
            </a-row>
          </div>
          <div>
            <a-form-model-item prop="apps">
              <div class="mb-4">
                <div class="d-flex align-items-center">
                  <span class="media-form--title required-label mr-3">Apps</span>
                  <a-tooltip v-if="form.sectionType === 'viewscreens'" slot="suffix"
                    title="Inherited from selected viewscreen">
                    <a-icon type="info-circle" class="text-primary" />
                  </a-tooltip>
                  <a v-else @click.stop="openAddAppModal" class="add-btn">+ Add</a>
                </div>
                <AppsCollapse class="mt-2" v-if="form.apps" :apps="form.apps" :appsShown="4" />
              </div>
            </a-form-model-item>
          </div>
          <a-divider class="m-0 mb-3" />
          <div>
            <div class="d-flex flex-row align-items-center justify-content-between mb-2 position-relative">
              <span class="font-weight-bold font-size-16">Media</span>
              <LanguageSelect
                v-model="selectedLanguage"
                :langs="languages"
                :contentFilled="filledLangs"
                :error="formErrorState"
                short-button-label
                :style="{ top: 0 }"
                @copyFromDefault="handleCopyFromDefault"
                @clear="handleClearLang"
              />
            </div>
            <div>
              <div>
                <div class="media-form__banner--redirect__dialogue--text" style="max-width: 650px;">
                  <span class="text-bold required">Title</span>
                    <a-textarea v-model="selectedInfo.title" placeholder="Enter title" :autoSize="{ minRows: 2, maxRows: 4 }" :showCount="true" :maxLength="100" class="mt-2" />
                  <div class="media-form__text-count">{{
                    stripHtml(selectedInfo.title).length }} / 100</div>
                </div>
              </div>
            </div>
            <div class="d-flex flex-column mb-2">
              <div class="mb-3">
                <span class="font-weight-bold font-size-14 mr-3">Redirect</span>
                <a-switch v-model="selectedRedirect.isRedirectAvailable" @change="handleRedirectSwitch" />
              </div>
              <div v-if="selectedRedirect.isRedirectAvailable">
                <div class="d-flex flex-column redirect-list">
                  <a-radio-group name="radioGroup" v-model="selectedRedirect.redirectType" :disabled="loading"
                    @change="(event) => handleRedirectTypeChange(event)" class="mb-3">
                    <a-radio value="link" :disabled="isLinkRedirectDisabled"> Link </a-radio>
                    <a-radio value="dialogue"> Bottom dialogue </a-radio>
                  </a-radio-group>
                  <div v-if="selectedRedirect.redirectType === 'link'">
                    <a-row>
                      <a-col :span="3" class="mr-4">
                        <a-form-model-item :colon="false">
                          <template #label><span class="required-label">Link type</span></template>
                          <a-select v-model="selectedLinkRedirect.linkType" size="large" :disabled="loading" allow-clear
                            @change="(value) => handleLinkTypeChange('link')">
                            <template #placeholder><span style="color: #a1a1c2">Select link type</span></template>
                            <a-select-option value="custom"> Custom </a-select-option>
                            <a-select-option value="sport"> Sport </a-select-option>
                            <a-select-option value="match"> Match </a-select-option>
                          </a-select>
                        </a-form-model-item>
                      </a-col>
                      <a-col :span="7">
                        <div v-if="selectedLinkRedirect.linkType === 'custom'">
                          <a-form-model-item :colon="false">
                            <template #label>
                              <span class="required-label">Link</span>
                              <a-button @click="openAddLinkTemplatesModal" size="small" type="link"
                                style="padding-left: 8px !important; height: 15px; line-height: 18px;">Save
                                as a
                                template</a-button>
                            </template>
                            <a-select class="media-form__custom-select" :value="selectedLinkRedirect.link"
                              :disabled="loading" mode="tags"
                              @change="(value) => handleTemplateChange(value, 'redirect')"
                              @inputKeydown="(e) => handleLinkTypeChange(e)" optionLabelProp="label" size="large">
                              <template #placeholder><span
                                  style="color: #a1a1c2">https://www.olimp.bet/line/1/5956496/78487617</span></template>
                              <a-select-option v-for="linkTemplate in filteredLinkTemplates"
                                :key="String(linkTemplate.id)" :value="String(linkTemplate.url)"
                                :label="linkTemplate.url">
                                <div class="d-flex justify-content-between align-items-center">
                                  <div>
                                    <span style="display: block;">{{ linkTemplate.name }}</span>
                                    <span style="color: gray; font-size: 12px;">{{ linkTemplate.url.length > 60 ?
                                      linkTemplate.url.slice(0, 60) + '...' : linkTemplate.url }}</span>
                                  </div>
                                  <a-popconfirm overlayClassName="deleteTemplateOverlay" :disabled="false"
                                    :getPopupContainer="getPopupContainer" title="Sure to delete this template?"
                                    @confirm="(e) => deleteLinkTemplate(e, linkTemplate.id)">
                                    <a-icon class="text-danger delete-icon" type="delete" @click.stop />
                                  </a-popconfirm>
                                </div>
                              </a-select-option>
                            </a-select>
                          </a-form-model-item>
                        </div>
                        <div v-if="selectedLinkRedirect.linkType === 'match'">
                          <a-form-model-item :colon="false" label="Match link">
                            <a-input placeholder="https://www.olimp.bet/line/1/5956496/78487617" :disabled="loading"
                              v-model="selectedLinkRedirect.link" size="large" />
                          </a-form-model-item>
                        </div>
                        <div v-if="selectedLinkRedirect.linkType === 'sport'">
                          <a-form-model-item :colon="false" label="Sport">
                            <a-select v-model="selectedLinkRedirect.sportId" size="large" :disabled="loading"
                              allow-clear>
                              <template #placeholder><span style="color: #a1a1c2;">Select sport</span></template>
                              <a-select-option v-for="sport in sports" :key="sport.sport_id">
                                {{ sport.name }}
                              </a-select-option>
                            </a-select>
                          </a-form-model-item>
                        </div>
                      </a-col>
                    </a-row>
                  </div>
                  <div v-else>
                    <div class="media-form__informer mb-3">
                      <div class="d-flex flex-column">
                        <span class="mb-2 media-form--title">Image</span>
                        <div>
                          <a-upload list-type="picture-card" class="media-form__informer--uploader"
                            :show-upload-list="false" @change="handleImageUpload" :disabled="loading">
                            <div>
                              <div v-if="selectedDialogueImg.preview" class="media-form__informer--image">
                                <img :src="selectedDialogueImg.preview" />
                              </div>
                              <template v-else>
                                <a-icon :type="selectedDialogueImg.loading ? 'loading' : 'inbox'" class="text-primary"
                                  style="font-size: 48px" />
                                <div>
                                  Click or drag file to this area to upload<br />
                                  <div class="text-gray-7">
                                    File size 1160 x 414 px
                                  </div>
                                </div>
                              </template>
                            </div>
                          </a-upload>
                        </div>
                        <div class="media-form__banner--redirect__dialogue--title mt-3" style="max-width: 650px;">
                          <a-form-model-item :colon="false">
                            <template #label>
                              <span>
                                Title
                              </span>
                              <a-tooltip slot="suffix" title="This value should be inherited from the card preview"
                                :getPopupContainer="getPopupContainer">
                                <a-icon type="info-circle" class="text-primary" />
                              </a-tooltip>
                            </template>
                            <quill-editor v-model="selectedRedirect.dialogueRedirect.title"
                              @change="($event) => validateQuillLength($event, 50)" :options="quillEditorOptions"
                              style="width: 650px;" />
                            <div class="media-form__text-count">{{
                              stripHtml(selectedRedirect.dialogueRedirect.title).length }} / 50</div>
                          </a-form-model-item>
                        </div>
                        <div class="media-form__banner--redirect__dialogue--text" style="max-width: 650px;">
                          <span class="text-bold required">Text</span>
                          <quill-editor v-model="selectedRedirect.dialogueRedirect.text"
                            @change="($event) => validateQuillLength($event, 200)" :options="quillEditorOptions"
                            style="width: 650px;" />
                          <div class="media-form__text-count">{{
                            stripHtml(selectedRedirect.dialogueRedirect.text).length }} / 200</div>
                        </div>
                        <a-row>
                          <a-col :span="7">
                            <div class="">
                              <span class="font-weight-bold font-size-14 mr-3">Button</span>
                              <a-switch v-model="selectedDialogueRedirect.isDialogueButtonAvailable"
                                @change="handleDialogueButtonSwitch" />
                            </div>
                          </a-col>
                        </a-row>
                        <a-row v-if="selectedDialogueRedirect.isDialogueButtonAvailable" class="my-3">
                          <a-col :span="4" class="mr-4">
                            <a-form-model-item required :colon="false" label="Button name">
                              <a-input v-model="selectedDialogueButton.buttonName" placeholder="Button name"
                                size="large" :disabled="loading" />
                            </a-form-model-item>
                          </a-col>
                          <a-col :span="3" class="mr-4">
                              <a-form-model-item prop="buttonColor" :colon="false">
                                <template #label><span class="required-label">Button color</span></template>
                                <a-input placeholder="#112233" :disabled="loading"
                                  v-model="selectedDialogueButton.buttonColor" size="large">
                                </a-input>
                              </a-form-model-item>
                            </a-col>
                            <a-col :span="3">
                              <a-form-model-item prop="fontColor" :colon="false">
                                <template #label><span class="required-label">Font color</span></template>
                                <a-input placeholder="#112233" :disabled="loading" v-model="selectedDialogueButton.fontColor"
                                  size="large">
                                </a-input>
                              </a-form-model-item>
                            </a-col>
                        </a-row>
                        <a-row v-if="selectedDialogueRedirect.isDialogueButtonAvailable">
                          <a-col :span="3" class="mr-4">
                            <a-form-model-item :colon="false">
                              <template #label><span class="required-label">Link type</span></template>
                              <a-select v-model="selectedDialogueButton.linkType" size="large" :disabled="loading"
                                @change="(value) => handleLinkTypeChange('dialogue')" allow-clear>
                                <template #placeholder><span style="color: #a1a1c2">Select link
                                    type</span></template>
                                <a-select-option value="custom"> Custom </a-select-option>
                                <a-select-option value="sport"> Sport </a-select-option>
                                <a-select-option value="match"> Match </a-select-option>
                              </a-select>
                            </a-form-model-item>
                          </a-col>
                          <a-col :span="7">
                            <div v-if="selectedDialogueButton.linkType === 'custom'">
                              <a-form-model-item :colon="false">
                                <template #label>
                                  <span class="required-label">Link</span>
                                  <a-button @click="openAddLinkTemplatesModal" :disabled="loading" size="small"
                                    type="link"
                                    style="padding-left: 8px !important; height: 15px; line-height: 18px;">Save as a
                                    template</a-button>
                                </template>
                                <a-select class="media-form__custom-select" :value="selectedDialogueButton.link"
                                  :disabled="loading" mode="tags"
                                  @change="(value) => handleTemplateChange(value, 'dialogue')"
                                  @inputKeydown="(e) => handleLinkTypeChange('dialogue', e)" optionLabelProp="label"
                                  size="large">
                                  <template #placeholder><span
                                      style="color: #a1a1c2">https://www.olimp.bet/line/1/5956496/78487617</span></template>
                                  <a-select-option v-for="linkTemplate in filteredLinkTemplates" :key="linkTemplate.id"
                                    :value="String(linkTemplate.url)" :label="linkTemplate.url">
                                    <div class="d-flex justify-content-between align-items-center">
                                      <div class="link-container">
                                        <span class="link-name">{{ linkTemplate.name }}</span>
                                        <span class="link-url">{{ linkTemplate.url || '' }}</span>
                                      </div>
                                      <a-popconfirm overlayClassName="deleteTemplateOverlay" :disabled="false"
                                        :getPopupContainer="getPopupContainer" title="Sure to delete this template?"
                                        @confirm="(e) => deleteLinkTemplate(e, linkTemplate.id, linkTemplate.url)">
                                        <a-icon class="text-danger delete-icon" type="delete" @click.stop />
                                      </a-popconfirm>
                                    </div>
                                  </a-select-option>
                                </a-select>
                              </a-form-model-item>
                            </div>
                            <div v-if="selectedDialogueButton.linkType === 'match'">
                              <a-form-model-item :colon="false" label="Match link">
                                <a-input placeholder="https://www.olimp.bet/line/1/5956496/78487617" :disabled="loading"
                                  v-model="selectedDialogueButton.link" size="large" />
                              </a-form-model-item>
                            </div>
                            <div v-if="selectedDialogueButton.linkType === 'sport'">
                              <a-form-model-item :colon="false" label="Sport">
                                <a-select v-model="selectedDialogueButton.sportId" size="large" :disabled="loading"
                                  allow-clear>
                                  <template #placeholder><span style="color: #a1a1c2;">Select sport</span></template>
                                  <a-select-option v-for="sport in sports" :key="sport.sport_id">
                                    {{ sport.name }}
                                  </a-select-option>
                                </a-select>
                              </a-form-model-item>
                            </div>
                          </a-col>
                        </a-row>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </a-form-model>
      <div v-if="errors.oneFullInformer" style="color: red; margin-top: 8px;">At least one language should have title</div>
      <div v-if="errors.onlyRedirect" style="color: red; margin-top: 8px">Can't create informer with redirect but
        without title</div>
      <div v-if="errors.redirectColorLength" style="color: red; margin-top: 8px">Redirect color must be a valid
        7-character hex code (e.g., #FFFFFF)</div>
      <div v-if="errors.isLinkRedirectsValid" style="color: red; margin-top: 8px">All link redirects should have link type and link / sport itself</div>
      <div v-if="errors.isDialogueRedirectsValid" style="color: red; margin-top: 8px">All dialogue redirects should have text</div>
      <div v-if="errors.isdDialogueButtonValid" style="color: red; margin-top: 8px">All dialogue buttons should have button name, button and font color, link type and link / sport itself</div>
    </MainContainer>
    <a-modal v-model="addAppsModal" title="Add new apps" :footer="null" width="700px" :destroyOnClose="true">
      <AppsModal @selectApps="changeSelectedApps" @close="closeAddAppsModal" :modalAppsData="modalAppsData" />
    </a-modal>
    <a-modal v-model="addLinkTemplateModal" width="395px" :destroyOnClose="true" @ok="handleAddTemplatesModal">
      <template #title><span class="modal-title">Add new template</span></template>
      <a-form-model :model="addTemplateForm" ref="templateForm" :rules="templateRules">
        <a-form-model-item label="Name" prop="name" :colon="false">
          <a-input placeholder="Name" :disabled="loading" size="large" v-model="addTemplateForm.name"></a-input>
        </a-form-model-item>
        <a-form-model-item label="Link" prop="link" :colon="false">
          <a-input placeholder="https://www.olimp.bet/line/1/5956496/7848761" :disabled="loading" size="large"
            v-model="addTemplateForm.link"></a-input>
        </a-form-model-item>
      </a-form-model>
    </a-modal>
  </div>
</template>

<script>
import HeadSection from '@/components/main/headSection'
import MainContainer from '@/components/main/mainContainer'
import AppsModal from '@/components/custom/modals/appsModal.vue'
import ActivityPeriods from '@/components/custom/ActivityPeriods.vue'
import LanguageSelect from '@/components/custom/languageSelect.vue'
import AppsCollapse from '@/views/apps/media/components/appsCollapse.vue'
import SegmentsSelect from '@/components/custom/segments/segmentsSelect.vue'
import SegmentsLabel from '@/components/custom/segments/segmentsLabel.vue'
import ViewscreenService from '@/services/api/apiViewscreenService'
import { returnToLastList } from '@/services/utils/routerHelpers'
import MediaService from '@/services/api/apiMediaService'
import { quillEditor } from 'vue-quill-editor'
import apiClient from '@/services/axios'
import isEqual from 'lodash/isEqual'

import { DEFAULT_LANG } from '@/constants/lang'
import {
  initialForm,
  defaultInformerImg,
  defaultInformerInfo,
  defaultDialogueButton,
  defaultInformerRedirect,
  informerFields,
} from '@/constants/media/informers'

function getImageDimensions(file) {
  return new Promise((resolve) => {
    const img = document.createElement('img')
    img.src = URL.createObjectURL(file)
    img.onload = () => {
      resolve({
        width: img.naturalWidth,
        height: img.naturalHeight,
      })
    }
  })
}

const deepClone = (obj) => {
  return JSON.parse(JSON.stringify(obj))
}

function getBase64(img) {
  return new Promise((resolve) => {
    const reader = new FileReader()
    reader.addEventListener('load', () => resolve(reader.result))
    reader.readAsDataURL(img)
  })
}

export default {
  name: 'informersForm',
  components: {
    HeadSection,
    MainContainer,
    ActivityPeriods,
    AppsCollapse,
    AppsModal,
    quillEditor,
    LanguageSelect,
    SegmentsLabel,
    SegmentsSelect,
  },
  data() {
    return {
      form: { ...initialForm },
      content: {
        ru: {
          informerInfo: {
            ...defaultInformerInfo,
          },
          informerRedirect: {
            ...defaultInformerRedirect,
          },
        },
      },
      contentValidationResult: true,
      errors: {
        redirectColorLength: false,
        oneFullInformer: false,
        onlyRedirect: false,
        isLinkRedirectsValid: false,
        isDialogueRedirectsValid: false,
        isdDialogueButtonValid: false,
      },
      tags: [
        { value: 'new', name: 'New' },
        { value: 'promo', name: 'Promo' },
        { value: 'top', name: 'Top' },
        { value: 'tech', name: 'Tech' },
        { value: 'maintenance', name: 'Maintenance' },
        { value: 'naitive', name: 'Native content' },
        { value: 'no_tag', name: 'No tag' },
      ],
      contentTypeOptions: [
        { label: 'Partners', value: 'Partners' },
        { label: 'Promotion', value: 'Promotion' },
        { label: 'Editorial', value: 'Editorial' },
        { label: 'SMM', value: 'SMM' },
        { label: 'Product', value: 'Product' },
        { label: 'Special project', value: 'Special project' },
      ],
      sections: [
        { label: 'Live', value: 1 },
        { label: 'Line', value: 2 },
        { label: 'Promo', value: 3 },
        { label: 'Main Web', value: 4 },
        { label: 'Registration', value: 5 },
        { label: 'Side menu', value: 6 },
        { label: 'Bottom menu', value: 7 },
      ],
      loading: false,
      mainLoading: false,
      validationResult: true,
      addAppsModal: false,
      languages: ['ru'],
      selectedLanguage: 'ru',
      linkTemplates: [],
      sports: [],
      segments: [],
      viewscreens: [],
      addTemplateForm: {
        name: '',
        link: '',
      },
      isRedirectAvailable: false,
      checkAllLangs: false,
      addLinkTemplateModal: false,
      informerToEdit: null,
      isFillingForm: false,
      viewscreenName: '',
      rules: {
        name: [
          {
            required: true,
            message: 'Informer name is required',
            trigger: 'blur',
          },
        ],
        sectionType: [
          {
            required: true,
            message: 'Please select section type',
            trigger: 'blue',
          },
        ],
        informerTag: [
          {
            required: true,
            message: 'Informer tag is required',
            trigger: 'blur',
          },
        ],
        selectedViewscreens: [
          {
            type: 'array',
            required: true,
            message: 'Please select at least one viewscreen',
            trigger: 'change',
          },
        ],
        segments: [
          {
            type: 'array',
            required: true,
            message: 'Please select at least one segment',
            trigger: 'change',
          },
        ],
        sections: [
          {
            type: 'array',
            required: true,
            message: 'Please select at least one section',
            trigger: 'change',
          },
        ],
        apps: [
          {
            type: 'array',
            required: true,
            message: 'Applications are required',
            trigger: 'change',
          },
        ],
      },
      templateRules: {
        name: [
          {
            required: true,
            message: 'This field is required',
            trigger: 'blur',
          },
        ],
        link: [
          {
            required: true,
            message: 'This field is required',
            trigger: 'blur',
          },
        ],
      },
      modalAppsData: null,
      quillEditorOptions: {
        placeholder: 'Input description',
        modules: {
          toolbar: [
            ['bold', 'italic', 'underline'], // toggled buttons
            ['blockquote'],

            [{ list: 'ordered' }, { list: 'bullet' }],
            [{ indent: '-1' }, { indent: '+1' }], // outdent/indent
            [{ direction: 'rtl' }], // text direction
            [{ header: [1, 2, 3, 4, 5, 6, false] }],
            [{ color: [] }, { background: [] }], // dropdown with defaults from theme
            [{ align: [] }],

            ['link'],
          ],
        },
      },
    }
  },
  computed: {
    userLoading() {
      return this.$store.state?.user?.loading || false
    },
    isEditMode() {
      return this.$route.path.split('/').includes('edit')
    },
    informerId() {
      if (!this.isEditMode) {
        return null
      }
      return this.$route.params.id
    },
    informerUuid() {
      if (!this.informerToEdit) {
        return null
      }
      return this.informerToEdit.context.uuid
    },
    routeTitle() {
      return this.isViewscreen ? 'Main' : 'Informers'
    },
    informerTitle() {
      return this.isEditMode ? 'Edit informer' : 'New informer'
    },
    selectedContent() {
      return this.content[this.selectedLanguage]
    },
    selectedInfo() {
      return this.selectedContent.informerInfo
    },
    selectedRedirect() {
      return this.selectedContent.informerRedirect
    },
    selectedLinkRedirect() {
      return this.selectedRedirect.linkRedirect
    },
    selectedDialogueRedirect() {
      return this.selectedRedirect.dialogueRedirect
    },
    selectedDialogueImg() {
      return this.selectedDialogueRedirect.img
    },
    selectedDialogueButton() {
      return this.selectedDialogueRedirect.dialogueButton
    },
    selectedTitleLength() {
      if (this.selectedInfo.title) {
        return this.selectedInfo.title.length
      }
      return 0
    },
    isViewscreen() {
      if (this.$route.params.viewscreenId || this.$route.params.sectionId) {
        return true
      }
      return false
    },
    isSectionTypeDisabled() {
      return !!this.form.sections.length || !!this.form.selectedViewscreens.length || !!this.form.segments.length
    },
    isLinkRedirectDisabled() {
      return this.form.sectionType === 'viewscreens'
    },
    isDefaultContentFilled() {
      const { title } = this.content['ru'].informerInfo
      return title?.length
    },
    breadcrumbs() {
      const defaultRootLink = '/media/informers'
      const lastSavedUrl = localStorage?.getItem('lastFilterListUrl')
      const rootLink = lastSavedUrl?.startsWith(defaultRootLink) ? lastSavedUrl : defaultRootLink

      return [
        { name: this.isViewscreen ? 'Main' : 'Media', link: this.isViewscreen ? `/main/${this.$route.params.viewscreenId}` : rootLink },
        { name: this.isEditMode ? 'Edit informer' : 'New informer', link: undefined },
      ]
    },
    filteredLinkTemplates() {
      const uniqueTemplates = new Set()
      return this.linkTemplates.filter(template => {
        const url = template.url.trim()
        if (!url || uniqueTemplates.has(url)) {
          return false
        }
        uniqueTemplates.add(url)
        return true
      })
    },
    isInitialForm() {
      return isEqual(this.selectedContent, informerFields)
    },
    formErrorState() {
      if (this.isInitialForm) {
        return null
      }
      return this.contentValidationResult ? null : 'Form is not filled correctly'
    },
    filledLangs() {
      const obj = this.languages.reduce((obj, { name }) => {
        obj[name] = !isEqual(this.content[name], informerFields)
        return obj
      }, {})

      return obj
    },
  },
  watch: {
    'form.selectedViewscreens'(viewscreenIds) {
      const allApps = viewscreenIds
        .map(id => {
          const vs = this.viewscreens.find(v => v.id === id)
          if (!vs) {
            console.warn('Viewsreen not found:', id, this.viewscreens)
          }
          return vs?.applications || []
        })
        .flat()
      const uniqueApps = new Map()
      allApps.forEach(app => {
        if (app?.application_id) {
          uniqueApps.set(app.application_id, app)
        }
      })
      this.form.apps = Array.from(uniqueApps.values())
      if (!this.form.selectedViewscreens.length) {
        this.form.sections = []
      }
      if (this.isFillingForm) {
        return
      }
      this.selectedRedirect.redirectType = 'dialogue'
      this.validateApps()
    },
    sectionType() {
      this.form.apps = []
      this.selectedRedirect.redirectType = 'dialogue'
    },
    selectedContent: {
      deep: true,
      handler() {
        if (this.isInitialForm) {
          return
        }

        this.$nextTick(() => this.validateContentForm())
      },
    },
    form: {
      deep: true,
      handler() {
        if (this.isInitialForm) {
          return
        }

        this.$nextTick(() => this.validateContentForm())
      },
    },
    selectedLanguage() {
      if (this.isInitialForm) {
        return
      }

      this.validateContentForm()
    },
  },
  async created() {
    this.mainLoading = true
    await this.fetchLocalizations()
    this.fetchSports()
    this.fetchLinkTemplates()
    this.getSegments()
    await this.getViewscreens()

    if (this.$route.params.id) {
      this.informerToEdit = await this.getInformer()
      this.isFillingForm = true
      this.fillForm()
      this.$nextTick(() => {
        this.isFillingForm = false
      })
    }

    if (this.isViewscreen) {
      this.fetchViewscreenTab()
    }

    if (!this.informerToEdit) {
      this.selectedLanguage = this.languages[0].name
      this.initializeContent()
    }
    this.mainLoading = false
  },
  methods: {
    getPopupContainer() {
      return document.body
    },
    async save() {
      const isValid = await this.validateForm()
      const isPeriodsValidate = await this.$refs['activity-periods'].validate()
      if (!isValid || !isPeriodsValidate) {
        return false
      }
      if (!this.isEditMode) {
        return this.createInformer()
      }
      this.updateInformer()
    },
    async validateForm() {
      const isValid = await new Promise((resolve) => {
        this.$refs.form.validate(resolve)
      })

      let atLeastOneValid = false
      let onlyRedirect = false
      let allRedirectsColorValid = true
      let isLinkRedirectValid = true
      let isDialogueRedirectValid = true
      let isDialogueButtonValid = true

      for (const lang in this.content) {
        const currentLang = this.content[lang]
        const isRedirectAvailable = currentLang.informerRedirect.isRedirectAvailable
        const isTitleEmpty = currentLang.informerInfo.title === ''

        if (isRedirectAvailable && isTitleEmpty) {
          onlyRedirect = true
        }
        if (!isTitleEmpty) {
          atLeastOneValid = true
        }

        if (currentLang.informerRedirect?.isRedirectAvailable) {
          if (currentLang.informerRedirect.redirectType === 'link') {
            const linkRedirect = currentLang.informerRedirect.linkRedirect || {}
            const hasLinkType = !!linkRedirect.linkType
            const hasValidLinkOrSport =
          (linkRedirect.linkType === 'sport' && !!linkRedirect.sportId) ||
          (linkRedirect.linkType !== 'sport' && linkRedirect.link.length)

            if (!hasLinkType || !hasValidLinkOrSport) {
              isLinkRedirectValid = false
            }
          }

          if (currentLang.informerRedirect.redirectType === 'dialogue') {
            const dialogueRedirect = currentLang.informerRedirect.dialogueRedirect || {}

            if (!dialogueRedirect.text || dialogueRedirect.text.trim() === '') {
              isDialogueRedirectValid = false
            }

            if (dialogueRedirect.isDialogueButtonAvailable) {
              const btn = dialogueRedirect.dialogueButton || {}
              const hasBtnName = !!btn.buttonName && btn.buttonName.trim() !== ''
              const hasBtnLinkType = !!btn.linkType
              const hasBtnLinkOrSport =
            (btn.linkType === 'sport' && !!btn.sportId) ||
            (btn.linkType !== 'sport' && !!btn.link)
              if (!hasBtnName || !hasBtnLinkType || !hasBtnLinkOrSport) {
                isDialogueButtonValid = false
              }
              if (btn.buttonColor.length !== 7 || btn.fontColor.length !== 7) {
                allRedirectsColorValid = false
              }
            }
          }
        }
      }
      const errors = {
        oneFullInformer: !atLeastOneValid,
        onlyRedirect: onlyRedirect,
        isLinkRedirectsValid: !isLinkRedirectValid,
        isDialogueRedirectsValid: !isDialogueRedirectValid,
        isdDialogueButtonValid: !isDialogueButtonValid,
        redirectColorLength: !allRedirectsColorValid,
      }
      this.errors = { ...errors }
      if (!isValid || Object.values(this.errors).includes(true)) {
        return false
      }
      return true
    },
    async validateUploadedImage(file) {
      const isImage = file.type.split('/')[0] === 'image'
      const fileExtension = file.type.split('/')[1]

      if (!isImage) {
        this.$notification.error({
          message: 'You can only upload PNG, WEBP or JPG / JPEG file!',
        })
        return false
      }

      const isLt5M = file.size / 1024 / 1024 < 10

      if (!isLt5M) {
        this.$notification.error({
          message: 'Informer image must be smaller than 10MB!',
        })
        return false
      }

      if (isImage) {
        const { width, height } = await getImageDimensions(file)
        if (width !== 1160 || height !== 414) {
          this.$notification.error({
            message: 'Informer image must be 1160 x 414px!',
          })
          return false
        }
        if (fileExtension !== 'png' && fileExtension !== 'webp' && fileExtension !== 'jpeg') {
          this.$notification.error({
            message: 'Image can be PNG, WEBP or JPG / JPEG extension!',
          })
          return false
        }
      }

      return file
    },
    initializeContent() {
      this.content = this.languages.reduce((acc, language) => {
        return {
          ...acc,
          [language.name]: deepClone(informerFields),
        }
      }, {})
    },
    handleLangChange(selectedLangKey) {
      if (this.loading) {
        return
      }
      this.selectedLanguage = selectedLangKey
    },
    handleRedirectSwitch(checked) {
      if (!checked) {
        this.content[this.selectedLanguage].informerRedirect = deepClone(defaultInformerRedirect)
      }
      if (this.form.sectionType === 'viewscreens') {
        this.selectedRedirect.redirectType = 'dialogue'
      }
    },
    handleRedirectTypeChange(event) {
      this.content[this.selectedLanguage].informerRedirect = deepClone(defaultInformerRedirect)
      this.content[this.selectedLanguage].informerRedirect.isRedirectAvailable = true
      this.content[this.selectedLanguage].informerRedirect.redirectType = event.target.value
    },
    handleDialogueButtonSwitch(checked) {
      if (!checked) {
        this.content[this.selectedLanguage].informerRedirect.dialogueRedirect.dialogueButton = deepClone(defaultDialogueButton)
      }
    },
    handleTemplateChange(newTemplate, type) {
      const filtered = (newTemplate || []).filter(template => template !== '' && template !== undefined)
      const last = filtered[filtered.length - 1] || []
      const foundTemplate = this.linkTemplates.find(link => link.url === last)
      const templateName = foundTemplate ? foundTemplate.name : ''

      const target = type === 'redirect' ? this.selectedLinkRedirect : this.selectedDialogueButton
      target.link = last
      target.linkName = templateName
    },
    handleLinkTypeChange(type, event) {
      if (event && event.key !== undefined) {
        if (event.key.length !== 1) return
      }

      const target = type === 'link'
        ? this.selectedLinkRedirect
        : this.selectedDialogueButton

      Object.assign(target, { link: [], linkName: '', sportId: '' })
    },
    clearApps() {
      this.form.apps = []
    },
    handleSectionTypeChange() {
      this.clearApps()
      if (this.form.sectionType === 'viewscreens') {
        this.handleClearLang(this.selectedLanguage)
        this.selectedRedirect.redirectType = 'dialogue'
      }
    },
    async handleImageUpload(event) {
      if (event.file.status === 'uploading') {
        return
      }

      const file = event.file

      const isFileValid = await this.validateUploadedImage(
        event.file.originFileObj,
      )

      if (!isFileValid) {
        return
      }

      const fileId = file.uid

      const target = this.content[this.selectedLanguage].informerRedirect.dialogueRedirect.img

      Object.assign(target, {
        ...defaultInformerImg,
        id: fileId,
        loading: true,
        file,
      })

      try {
        const response = await MediaService.uploadImage(file)
        const previewId = response.data?.data?.id
        if (!previewId) {
          const errorMessage =
                        response.data?.error?.message ||
                        'Something went wrong while uploading informer image'
          Object.assign(target, { ...defaultInformerImg })
          this.$notification.error({
            message: errorMessage,
          })
        }
        target.id = previewId
        target.preview = await getBase64(event.file.originFileObj)
        target.loading = false
      } catch (e) {
        const errorMessage =
                    e.response.data?.error?.message ||
                    'Something went wrong while uploading the preview'
        Object.assign(target, { ...defaultInformerImg })
        this.$notification.error({
          message: errorMessage,
        })
      }
    },
    validateQuillLength(e, maxLength) {
      const text = e.text.trim() ?? ''

      if (maxLength && maxLength < text.length) {
        e.quill.setText(text.substring(0, maxLength))
      }
    },
    stripHtml(htmlString) {
      const tempDiv = document.createElement('div')
      tempDiv.innerHTML = htmlString

      return tempDiv.textContent || tempDiv.innerText || ''
    },
    fillForm() {
      const { name, external_id, content_type, segments, applications, displays, is_active, tag, images } = this.informerToEdit.context

      this.form.name = name
      this.form.extId = external_id
      this.form.contentType = content_type
      this.form.apps = applications
      this.modalAppsData = applications
      if (this.informerToEdit.viewscreens?.length) {
        this.form.selectedViewscreens = this.informerToEdit.viewscreens
      } else {
        this.form.sectionType = 'screens'
        this.form.segments = segments.map(segment => segment.id)
      }
      if (displays.length) {
        this.form.sections = displays.map((display) => {
          return display.id
        })
      }
      this.form.isActive = is_active
      this.form.informerTag = tag
      const informerLanguages = Object.keys(images)
      this.selectedLanguage = informerLanguages[0]
      const informerContent = informerLanguages.reduce((acc, language) => {
        const informer = images[language]
        const redirect = informer.redirect || {}
        if (!Object.keys(informer).length) return acc
        const content = {
          informerInfo: {
            title: informer.title,
          },
          informerRedirect: {
            isRedirectAvailable: !!redirect.type,
            redirectType: redirect.type || 'link',
            linkRedirect: {
              linkType: 'custom',
              linkName: '',
              sportId: '',
              link: [],
              id: '',
            },
            dialogueRedirect: {
              img: {
                id: null,
                preview: '',
                url: '',
              },
              title: '',
              text: '',
              isDialogueButtonAvailable: false,
              dialogueButton: {
                buttonName: '',
                buttonColor: '',
                fontColor: '',
                linkType: 'custom',
                linkName: '',
                sportId: '',
                link: [],
                id: '',
              },
            },
          },
        }
        if (redirect.type === 'link') {
          const link = redirect.link || {}
          content.informerRedirect.linkRedirect = {
            linkType: link.type || 'custom',
            linkName: link.name || '',
            sportId: link.sport_id || '',
            link: link.url || '',
            id: link.id || '',
          }
        }

        if (redirect.type === 'dialogue') {
          const dialogue = redirect.dialogue || {}
          content.informerRedirect.dialogueRedirect.title = dialogue.title || ''
          content.informerRedirect.dialogueRedirect.text = dialogue.body || ''
          if (dialogue.image?.id) {
            content.informerRedirect.dialogueRedirect.img = {
              id: dialogue.image.id,
              preview: dialogue.image.path,
              url: dialogue.image.path,
            }
          }

          if (dialogue.button) {
            content.informerRedirect.dialogueRedirect.isDialogueButtonAvailable = true
            content.informerRedirect.dialogueRedirect.dialogueButton.buttonName = dialogue.button.name || ''
            content.informerRedirect.dialogueRedirect.dialogueButton.buttonColor = dialogue.button.button_color || ''
            content.informerRedirect.dialogueRedirect.dialogueButton.fontColor = dialogue.button.font_color || ''

            const btnRedirect = dialogue.button.redirect?.link || {}
            content.informerRedirect.dialogueRedirect.dialogueButton.linkType = btnRedirect.type || 'custom'
            content.informerRedirect.dialogueRedirect.dialogueButton.linkName = btnRedirect.name || ''
            content.informerRedirect.dialogueRedirect.dialogueButton.link = btnRedirect.url || ''
            content.informerRedirect.dialogueRedirect.dialogueButton.sportId = btnRedirect.sport_id || ''
          }
        }
        acc[language] = content
        return acc
      }, {})
      const defaultContent = this.languages.reduce((acc, language) => {
        return {
          ...acc,
          [language.name]: {
            informerInfo: { ...deepClone(defaultInformerInfo) },
            informerRedirect: { ...deepClone(defaultInformerRedirect) },
          },
        }
      }, {})

      this.content = {
        ...defaultContent,
        ...informerContent,
      }
    },
    async addInformerSectionItem(informerId) {
      this.loading = true
      try {
        const response = await ViewscreenService.addViewscreenSectionItem(this.$route.params.sectionId, informerId)
        if (response.data.data) {
          this.$notification.success({
            message: 'Informer has been successfully added to the viewscreen section',
          })
        }
      } catch (e) {
        this.$notification.error({
          message: 'Error while adding informer to viewscreen section',
          description: e.response.data.error.message,
        })
        this.loading = false
      } finally {
        this.loading = false
      }
    },
    getInformerContentByLanguage() {
      return Object.entries(this.content).reduce((acc, [language, content]) => {
        const { title } = content.informerInfo
        const informerInfo = {
          title: title,
        }
        const redirect = content.informerRedirect

        if (redirect.isRedirectAvailable) {
          const redirectType = redirect.redirectType
          if (redirectType === 'link') {
            const { linkType, linkName, sportId, link } = redirect.linkRedirect

            informerInfo.redirect = {
              type: 'link',
              link: {
                type: linkType,
                url: link.length !== 0 ? link : '',
              },
            }

            if (linkType === 'custom') {
              informerInfo.redirect.link.name = linkName
            }

            if (linkType === 'sport') {
              informerInfo.redirect.link.sport_id = sportId
            }
          }
          if (redirectType === 'dialogue') {
            const dialogue = redirect.dialogueRedirect
            const dialogueData = {
              ...(dialogue.title.length !== 0 && { title: dialogue.title }),
              body: dialogue.text || '',
              ...(dialogue.img.preview !== '' && { image_id: dialogue.img.id }),
            }
            if (dialogue.isDialogueButtonAvailable) {
              const btn = dialogue.dialogueButton
              const buttonRedirect = {
                type: 'link',
                link: {
                  type: btn.linkType,
                  url: btn.link.length !== 0 ? btn.link : '',
                },
              }

              if (btn.linkType === 'custom') {
                buttonRedirect.link.name = btn.linkName
              }

              if (btn.linkType === 'sport') {
                buttonRedirect.link.sport_id = btn.sportId
              }
              dialogueData.button = {
                name: btn.buttonName || '',
                button_color: btn.buttonColor,
                font_color: btn.fontColor,
                redirect: buttonRedirect,
              }
            }
            informerInfo.redirect = {
              type: 'dialogue',
              dialogue: dialogueData,
            }
          }
        }
        if (title !== '') {
          return {
            ...acc,
            [language]: informerInfo,
          }
        }
        return acc
      }, {})
    },
    getInformerPayload() {
      const informerPayload = {
        name: this.form.name,
        ...(this.form.extId?.trim() !== '') && { external_id: this.form.extId },
        ...(this.form.contentType !== undefined && { content_type: this.form.contentType }),
        periods: this.$refs['activity-periods'].getPeriods(),
        ...(this.form.sectionType === 'viewscreens' && { viewscreens: this.form.selectedViewscreens }),
        ...(this.form.sectionType === 'screens' && { applications: this.getApplications() }),
        ...(this.form.sectionType === 'screens' && { displays: this.form.sections }),
        ...(this.form.sectionType === 'screens' && { segments: this.form.segments }),
        is_active: this.form.isActive,
        tag: this.form.informerTag,
        images: this.getInformerContentByLanguage(),
      }
      return informerPayload
    },
    clearForm() {
      this.form = { ...initialForm }
      this.redirect = { ...defaultInformerRedirect }
      this.initializeContent()
    },
    getApplications() {
      return this.form.apps.map((app) => {
        return {
          id: app.application_id,
          ...(app.min_app_version !== '' && { min_app_version: app.min_app_version }),
          ...(app.max_app_version !== '' && { max_app_version: app.max_app_version }),
        }
      })
    },
    async validateSegments() {
      if (!this.$refs.form) return true
      const isValid = await new Promise((resolve) => {
        this.$refs.form.validateField('segments', resolve)
      })
      return isValid
    },
    async validateApps() {
      if (!this.$refs.form) return true
      const isValid = await new Promise((resolve) => {
        this.$refs.form.validateField('apps', resolve)
      })
      return isValid
    },
    openAddAppModal() {
      if (this.loading) {
        return
      }
      this.addAppsModal = true
    },
    closeAddAppsModal() {
      this.addAppsModal = false
    },
    changeSelectedApps(apps) {
      this.closeAddAppsModal()
      this.form.apps = apps
      this.modalAppsData = apps
      this.validateApps()
    },
    openAddLinkTemplatesModal() {
      if (this.loading) {
        return
      }
      this.addLinkTemplateModal = true
    },
    closeAddLinkTemplatesModal() {
      this.addLinkTemplateModal = false
    },
    redirectToRoute() {
      if (this.isViewscreen) {
        return this.redirectToViewscreen()
      }
      return this.redirectToMedia()
    },
    async deleteLinkTemplate(event, templateId, deletedlink) {
      try {
        const linkResponse = await MediaService.deleteLink(templateId)

        if (!linkResponse || linkResponse.success === false) {
          this.$notification.error({
            message: 'Error while deleting link',
            description: linkResponse?.data.error || 'Unknown error',
          })
          return
        }
        this.$notification.success({
          message: 'Link deleted successfully',
        })
        this.linkTemplates = this.linkTemplates.filter(link => link.id !== templateId)
        if (this.selectedLinkRedirect.link === deletedlink) {
          this.selectedLinkRedirect.link = []
        }
        if (this.selectedDialogueButton.link === deletedlink) {
          this.selectedDialogueButton.link = []
        }
      } catch (e) {
        this.$notification.error({
          message: 'Error while deleting link',
          description: e.response.data.error.message,
        })
      }
      event.stopPropagation()
    },
    redirectToViewscreen() {
      this.$router.push(`/main/${this.$route.params.viewscreenId}`)
    },
    redirectToMedia() {
      returnToLastList(this.$router, '/media/informers')
    },
    validateFormState() {
      if (!this.$refs.form) return

      this.$refs.form.validate((valid) => {
        this.validationResult = valid
      })
    },
    async getInformer() {
      try {
        this.loading = true
        const informerResponse = await MediaService.getInformer(this.$route.params.id)
        this.loading = false
        return informerResponse.data.data
      } catch (err) {
        this.$notification.error({
          message: err.response.data.error.message,
        })
        this.loading = false
      }
    },
    async createInformer() {
      try {
        this.mainLoading = true
        const informerPayload = this.getInformerPayload()

        const informerResponse = await MediaService.createInformer(informerPayload)
        if (informerResponse?.data?.error || informerResponse.data.success === false || informerResponse.data.error) {
          this.$notification.error({
            message: 'Error while creating informer',
            description: informerResponse?.data?.error?.message || '',
          })
          this.mainLoading = false
          this.loading = false
          return
        }
        this.$notification.success({
          message: 'Informer successfully created',
        })
        if (this.isViewscreen) {
          await this.addInformerSectionItem(informerResponse.data.data.context.id)
        }
        this.redirectToRoute()
      } catch (err) {
        this.$notification.error({
          message: err.response.data.error.message,
        })
        this.mainLoading = false
        this.loading = false
      } finally {
        this.mainLoading = false
        this.loading = false
      }
    },
    async updateInformer() {
      try {
        this.mainLoading = true
        const informerPayload = this.getInformerPayload()

        const informerResponse = await MediaService.updateInformer(this.informerId, informerPayload)
        if (informerResponse.data.data) {
          this.$notification.success({
            message: 'Informer successfully updated',
          })
          this.redirectToRoute()
        }
      } catch (err) {
        this.$notification.error({
          message: err.response.data.error.message,
        })
        this.loading = false
        this.mainLoading = false
      } finally {
        this.loading = false
        this.mainLoading = false
      }
    },
    async deleteInformer() {
      try {
        const response = await MediaService.deleteInformer(this.informerId)
        if (response.data.data) {
          this.$notification.success({
            message: 'Informer successfully deleted',
          })
          this.redirectToRoute()
        }
      } catch (err) {
        this.$notification.error({
          message: err.response.data.error.message,
        })
      }
    },
    async handleAddTemplatesModal() {
      try {
        this.loading = true
        const templateResponse = await apiClient.post(
          '/admin/links',
          this.addTemplateForm,
        )
        this.linkTemplates.push(templateResponse.data.data)
        this.closeAddLinkTemplatesModal()
        this.$refs.templateForm.resetFields()
        if (templateResponse.data.error) {
          this.loading = false
          this.$notification.error({
            message: 'Error while adding templates',
            description: templateResponse.data.error.message,
          })
          return
        }
      } catch (e) {
        this.$notification.error({
          message: 'Error while adding templates',
          description: e.response.data.error.message,
        })
        this.loading = false
      }
      this.loading = false
    },
    async fetchLocalizations() {
      this.languagesLoading = true
      const url = '/admin/localizations'
      try {
        const response = await apiClient.get(url)
        this.languages = response.data.data
      } catch (error) {
        console.log('Error while trying to get locales', error)
      } finally {
        this.languagesLoading = false
      }
    },
    async fetchSports() {
      this.loading = true
      const url = '/admin/sports?results=1000'
      try {
        const response = await apiClient.get(url)
        this.sports = response.data.data
      } catch (error) {
        console.log('Error while trying to get sports', error)
      } finally {
        this.loading = false
      }
    },
    async fetchLinkTemplates() {
      this.loading = true
      const url = '/admin/links?type=custom&results=1000'
      try {
        const response = await apiClient.get(url)
        this.linkTemplates = response.data.data
      } catch (error) {
        console.log('Error while trying to get templates', error)
      } finally {
        this.loading = false
      }
    },
    async fetchViewscreenTab() {
      this.loading = true
      try {
        const response = await ViewscreenService.getViewscreenTab(this.$route.params.viewscreenId, '0')
        if (response.data.data) {
          this.viewscreenName = response.data.data.name
        }
      } finally {
        this.loading = false
      }
    },
    async getSegments() {
      this.loading = true
      try {
        const response = await ViewscreenService.getSegmentatorSegments()
        if (response.data.data) {
          this.segments = response.data.data
        }
      } finally {
        this.loading = false
      }
    },
    async getViewscreens() {
      this.loading = true
      try {
        const response = await ViewscreenService.getViewscreenTabs('', 'informer')
        if (response.data.data) {
          this.viewscreens = response.data.data.filter(
            (viewscreen) => !viewscreen.is_default)
        }
      } finally {
        this.loading = false
      }
    },
    handleCopyFromDefault() {
      if (!this.content[DEFAULT_LANG] || !this.content[this.selectedLanguage]) {
        return
      }

      const defaultForm = this.content[DEFAULT_LANG]

      this.content[this.selectedLanguage] = JSON.parse(
        JSON.stringify(defaultForm),
      )
    },
    handleClearLang(lang) {
      this.content[lang] = Object.assign({}, informerFields)

      this.$refs.form.clearValidate()
    },
    validateContentForm() {
      if (!this.$refs.form) return

      this.$refs.form.validate((valid) => {
        this.contentValidationResult = valid
      })
    },
  },
}
</script>

<style lang="scss" src="../../style.module.scss" />
